/* eslint-disable react-hooks/error-boundaries */
import React, { useEffect, useRef } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { ArrowBackIcon } from '@depop/web-ui-kit/Icons/ArrowBackIcon';
import { CancelIcon } from '@depop/web-ui-kit/Icons/CancelIcon';
import { useTranslations } from 'next-intl';

import { CategoryList } from './CategoryList';
import { PopularTiles } from './PopularTiles';
import styles from './styles.module.css';

import { isNotNull } from '@/modules/cms/helpers/isNotNull';
import {
  type CategoryList as MeganavCategoryList,
  type ImageLinkList,
  MegaNavContentType,
  type MegaNavItem,
} from '@/modules/meganav/types';

type Props = {
  isOpen: boolean;
  closeMenu: () => void;
  items: MegaNavItem['contentCollection']['items'];
  onBackClick: () => void;
  title: string;
  focusOnOpen?: boolean;
};

export function SubMenu({
  isOpen,
  closeMenu,
  items,
  onBackClick,
  title,
  focusOnOpen = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && focusOnOpen) {
      containerRef.current?.focus();
    }
  }, [isOpen, focusOnOpen]);

  const t = useTranslations('common');
  const tabIndex = isOpen ? 0 : -1;

  function renderContent(item: MeganavCategoryList | ImageLinkList) {
    switch (item.__typename) {
      case MegaNavContentType.CategoryList:
        return <CategoryList isOpen={isOpen} {...item} />;
      case MegaNavContentType.ImageLinkList:
        return <PopularTiles {...item} />;
      default:
        return null;
    }
  }

  try {
    return (
      <div
        className={isOpen ? styles['container--open'] : styles.container}
        tabIndex={tabIndex}
        role="dialog"
        ref={containerRef}
      >
        <div className={styles.header}>
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={onBackClick}
              tabIndex={tabIndex}
              aria-label={t('Back')}
            >
              <Body variant="sm-regular" as="span">
                <ArrowBackIcon />
              </Body>
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={closeMenu}
              tabIndex={tabIndex}
              aria-label={t('Close')}
            >
              <Body variant="sm-regular" as="span">
                <CancelIcon />
              </Body>
            </button>
          </div>
          {Boolean(title) && <Heading variant="md">{title}</Heading>}
        </div>
        {items.filter(isNotNull).map((item) => (
          <React.Fragment key={JSON.stringify(item)}>
            {renderContent(item)}
          </React.Fragment>
        ))}
      </div>
    );
  } catch (error) {
    // log error to Datadog
    const renderError = new Error((error as Error).message);
    renderError.name = 'NavigationV2_RENDER_ERROR';
    renderError.cause = error;
    datadogRum.addError(renderError);
    return null;
  }
}
